import { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import {
	MagnifyingGlassIcon,
	PlusIcon,
	XMarkIcon,
} from "@heroicons/react/24/outline";
import { TrashIcon } from "@heroicons/react/20/solid";
import { Trash, Loader2 } from "lucide-react";
import { apiClient } from "../../api";
import { useProperty } from "../../Contexts/PropertyContext/PropertyContext";
import { useAuth } from "../../Contexts/AuthContext/AuthContext";
import { baseUrl } from "../../Environments/environment";

const MEMBER_ROLES = {
	VIEWER: "viewer",
	EDITOR: "editor",
	FULL_ACCESS: "full_access",
	ADMIN: "admin",
};

const ROLE_DESCRIPTIONS = {
	viewer: "Read-only access. Cannot manage the team.",
	editor: "Full access except team management.",
	full_access: "Full access including team management, except modifying admins.",
	admin: "Full control, including managing other admins.",
};

const ROLE_BADGE_STYLES = {
	admin: {
		bg: "bg-rose-50",
		text: "text-rose-700",
		ring: "ring-rose-600/20",
		dot: "bg-rose-500",
		border: "border-rose-200",
	},
	full_access: {
		bg: "bg-indigo-50",
		text: "text-indigo-700",
		ring: "ring-indigo-600/20",
		dot: "bg-indigo-500",
		border: "border-indigo-200",
	},
	editor: {
		bg: "bg-sky-50",
		text: "text-sky-700",
		ring: "ring-sky-600/20",
		dot: "bg-sky-500",
		border: "border-sky-200",
	},
	viewer: {
		bg: "bg-slate-50",
		text: "text-slate-700",
		ring: "ring-slate-600/20",
		dot: "bg-slate-500",
		border: "border-slate-200",
	},
};

const SELECTED_ROLE_BUTTON_STYLES = {
	admin: "bg-rose-600 text-white ring-2 ring-rose-600 shadow-lg shadow-rose-600/20 scale-[1.03] border-transparent",
	full_access: "bg-indigo-600 text-white ring-2 ring-indigo-600 shadow-lg shadow-indigo-600/20 scale-[1.03] border-transparent",
	editor: "bg-sky-600 text-white ring-2 ring-sky-600 shadow-lg shadow-sky-600/20 scale-[1.03] border-transparent",
	viewer: "bg-slate-700 text-white ring-2 ring-slate-700 shadow-lg shadow-slate-700/20 scale-[1.03] border-transparent",
};

function RoleBadge({ role }) {
	const style = ROLE_BADGE_STYLES[role] || ROLE_BADGE_STYLES.viewer;
	return (
		<span className={`inline-flex items-center justify-center gap-1.5 w-[125px] rounded-full ${style.bg} py-1 text-[11px] font-bold ${style.text} ring-1 ring-inset ${style.ring} whitespace-nowrap`}>
			<span className={`h-1.5 w-1.5 rounded-full ${style.dot}`} />
			{(role || "").replace("_", " ").toUpperCase()}
		</span>
	);
}

function RoleDropdown({ value, onChange }) {
	const style = ROLE_BADGE_STYLES[value] || ROLE_BADGE_STYLES.viewer;
	return (
		<select
			value={value}
			onChange={(e) => onChange(e.target.value)}
			className={`w-[125px] rounded-full border ${style.border} ${style.bg} py-1 text-center text-[11px] font-bold ${style.text} focus:outline-none cursor-pointer transition-all whitespace-nowrap`}
		>
			{Object.entries(MEMBER_ROLES).map(([key, val]) => (
				<option key={key} value={val} className="text-gray-900 font-normal bg-white">
					{val.replace("_", " ").toUpperCase()}
				</option>
			))}
		</select>
	);
}
const fetchUsers = async (propertyId) => {
	try {
		const response = await apiClient.get(`/user/${propertyId}`);
		return response.data.result;
	} catch (error) {
		console.error("Error fetching users:", error);
		return [];
	}
};

function TruncatedText({ text, className, maxClass }) {
	const [isTruncated, setIsTruncated] = useState(false);
	const textRef = useRef(null);

	const checkTruncation = () => {
		if (textRef.current) {
			setIsTruncated(textRef.current.scrollWidth > textRef.current.clientWidth);
		}
	};

	useEffect(() => {
		checkTruncation();
		window.addEventListener("resize", checkTruncation);
		return () => window.removeEventListener("resize", checkTruncation);
	}, [text]);

	return (
		<div className="relative group min-w-0" onMouseEnter={checkTruncation}>
			<span
				ref={textRef}
				className={`${className} ${maxClass} truncate block cursor-default`}
			>
				{text}
			</span>
			{isTruncated && (
				<div className="absolute left-0 bottom-full mb-2 hidden group-hover:block bg-slate-900 text-white text-[11px] font-medium px-2.5 py-1.5 rounded-lg shadow-xl whitespace-nowrap z-50 pointer-events-none">
					{text}
					<div className="absolute top-full left-4 -translate-y-px border-4 border-transparent border-t-slate-900" />
				</div>
			)}
		</div>
	);
}

export default function TeamMemberManagement() {
	const [isInviteOpen, setIsInviteOpen] = useState(false);
	const [isLinkQROpen, setIsLinkQROpen] = useState(false);
	const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
	const [selectedMember, setSelectedMember] = useState(null);
	const [memberToRemove, setMemberToRemove] = useState(null);
	const [emails, setEmails] = useState("");
	const [inviteRole, setInviteRole] = useState("");
	const [updatedRoles, setUpdatedRoles] = useState({});
	const [isLoading, setIsLoading] = useState(false);
	const [searchQuery, setSearchQuery] = useState("");
	const [isListLoading, setIsListLoading] = useState(true);

	const { selectedPropertyId: propertyId } = useProperty();
	const { user } = useAuth();
	const [members, setMembers] = useState([]);
	const [qrs, setQrs] = useState([]);

	const loggedInMember = members.find(m => m._id === user?._id);
	const loggedInRole = loggedInMember?.role || "viewer";
	const canManageTeam = ["admin", "full_access"].includes(loggedInRole);

	const emailTrimmed = emails.trim();
	const isValidEmailFormat = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(emailTrimmed);
	const showEmailError = emailTrimmed.length > 0 && !isValidEmailFormat;

	const loadQRs = async () => {
		if (!propertyId) return;
		try {
			const response = await apiClient.get(`/api/qrs?propertyId=${propertyId}&limit=1000`);
			if (response.status === 200) {
				setQrs(response.data.result.result);
			}
		} catch (error) {
			console.error("Error fetching QRs:", error);
		}
	};

	const handleLinkQR = async (qrId) => {
		try {
			setIsLoading(true);
			const response = await apiClient.patch(`/api/qrs/${qrId}`, {
				userId: selectedMember._id,
			});
			if (response.status === 200) {
				toast.success(`QR linked to ${selectedMember.name}`);
				setIsLinkQROpen(false);
				loadQRs();
			}
		} catch (error) {
			toast.error("Failed to link QR");
		} finally {
			setIsLoading(false);
		}
	};

	const handleUnlinkQR = async (qrId) => {
		try {
			setIsLoading(true);
			const response = await apiClient.patch(`/api/qrs/${qrId}`, {
				userId: null,
			});
			if (response.status === 200) {
				toast.success("QR unlinked");
				loadQRs();
			}
		} catch (error) {
			toast.error("Failed to unlink QR");
		} finally {
			setIsLoading(false);
		}
	};

	const updateAccess = (id, level, member) => {
		setMembers((prev) =>
			prev.map((m) => (m._id === id ? { ...m, role: level } : m)),
		);

		setUpdatedRoles((prev) => ({
			...prev,
			[id]: {
				role: level,
				type: member?.type || "member",
			},
		}));
	};
	const handleSave = async () => {
		try {
			if (Object.keys(updatedRoles).length === 0) return;
			setIsLoading(true);

			await apiClient.put(`/user/${propertyId}/updateRoles`, {
				payload: members,
			});

			setUpdatedRoles({});
			toast.success("Team roles updated!");
			loadUsers();
		} catch (error) {
			console.error("Error saving roles:", error);
			toast.error("Failed to update roles");
		} finally {
			setIsLoading(false);
		}
	};

	const handleDiscard = () => {
		loadUsers();
		setUpdatedRoles({});
		toast.info("Unsaved changes discarded.");
	};

	const handleInvite = async () => {
		const trimmedEmail = emails.trim();
		if (!isValidEmailFormat || !trimmedEmail) {
			toast.error("Please enter a valid email address.");
			return;
		}

		const isAlreadyMember = members.some((member) => (member.email || "").toLowerCase() === trimmedEmail.toLowerCase());
		if (isAlreadyMember) {
			toast.error("This email is already added to the team.");
			return;
		}
		if (!inviteRole) {
			toast.error("Please select a role.");
			return;
		}
		try {
			setIsLoading(true);
			await apiClient.post(
				`/userInvite/${propertyId}/sendInviteEmails`,
				{
					emails: [emails.trim()],
					role: inviteRole,
				},
			);
			toast.success("Invites sent successfully!");
			loadUsers();
			setIsInviteOpen(false);
			setEmails("");
			setInviteRole("");
		} catch (e) {
			console.error("Error sending invites:", e);
			toast.error(e.response?.data?.message || "Error sending invites");
		} finally {
			setIsLoading(false);
		}
	};

	const loadUsers = async () => {
		try {
			setIsListLoading(true);
			const result = await fetchUsers(propertyId); // Replace with actual property ID
			setMembers(result);
		} catch (error) {
			console.error("Error fetching users:", error);
		} finally {
			setIsListLoading(false);
		}
	};
	useEffect(() => {
		if (!propertyId) return;
		loadUsers();
		loadQRs();
	}, [propertyId]);

	const resendInvite = async (email) => {
		try {
			setIsLoading(true);
			await apiClient.post(`/userInvite/${propertyId}/resendInvite`, {
				email,
			});
			toast.success("Invitation resent successfully!");
		} catch (error) {
			console.error("Error resending invite:", error);
			toast.error(error.response?.data?.message || "Failed to resend invite");
		} finally {
			setIsLoading(false);
		}
	};

	const handleRemoveMember = (member) => {
		setMemberToRemove(member);
		setIsRemoveModalOpen(true);
	};

	const confirmRemove = async () => {
		if (!memberToRemove) return;

		try {
			setIsLoading(true);
			await apiClient.delete(`/user/${propertyId}/revoke/${memberToRemove._id}?type=${memberToRemove.type || "active"}`);
			toast.success("Member removed successfully!");
			setIsRemoveModalOpen(false);
			setMemberToRemove(null);
			loadUsers();
		} catch (error) {
			console.error("Error removing member:", error);
			toast.error("Failed to remove member");
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<div className="bg-gray-100 p-4 sm:p-6 lg:p-8 xl:p-12 min-h-screen font-poppins">
			<div className="mx-auto max-w-full">
				{/* Header */}
				<div className="flex items-center justify-between">
					<div>
						<h1 className="text-2xl font-semibold text-gray-900 tracking-tight">Your Team</h1>
						<p className="mt-1 text-sm text-gray-500">
							Manage team access and linked review QR codes.
						</p>
					</div>

					<div className="flex items-center gap-3">
						{canManageTeam && (
							<>
								<button
									onClick={handleSave}
									disabled={Object.keys(updatedRoles).length === 0 || isLoading}
									className={`rounded-xl px-6 py-2.5 text-sm font-semibold transition-all duration-200 transform active:scale-95 ${Object.keys(updatedRoles).length === 0 || isLoading
										? "bg-gray-200 text-gray-400 cursor-not-allowed"
										: "bg-indigo-600 text-white hover:bg-indigo-700 shadow-md hover:shadow-indigo-200"
										}`}
								>
									{isLoading ? "Saving..." : "Save Changes"}
								</button>

								<button
									onClick={() => {
										setIsInviteOpen(true);
										setEmails("");
										setInviteRole("");
									}}
									className="inline-flex items-center gap-2 rounded-xl bg-black px-6 py-2.5 text-sm font-semibold text-white hover:bg-gray-900 shadow-md hover:shadow-gray-300 transition-all duration-200 transform active:scale-95"
								>
									<PlusIcon className="h-4 w-4" />
									Invite Member
								</button>
							</>
						)}
					</div>
				</div>

				{/* Card */}
				<div className="mt-8 rounded-3xl bg-white shadow-xl shadow-gray-200/50 border border-gray-100 overflow-hidden">
					<div className="border-b border-gray-100 bg-gray-50/50 px-8 py-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
						{/* Left Side: Title and Search Bar */}
						<div className="flex flex-col sm:flex-row sm:items-center gap-4 flex-1">
							<h2 className="text-sm font-bold text-gray-900 uppercase tracking-wider whitespace-nowrap">
								Team Directory
							</h2>

							{/* Beautiful Minimalist Search Bar - Left Aligned */}
							<div className="relative w-full max-w-xs sm:max-w-sm">
								<div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
									<MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
								</div>
								<input
									type="text"
									value={searchQuery}
									onChange={(e) => setSearchQuery(e.target.value)}
									placeholder="Search team members by name or email..."
									className="w-full rounded-2xl border border-gray-100 bg-white/80 py-2.5 pl-9 pr-8 text-xs font-semibold text-gray-900 placeholder-gray-400 focus:bg-white focus:border-indigo-500 focus:outline-none focus:ring-4 focus:ring-indigo-500/5 transition-all shadow-sm"
								/>
								{searchQuery && (
									<button
										onClick={() => setSearchQuery("")}
										className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 transition-colors"
										title="Clear Search"
									>
										<XMarkIcon className="h-4 w-4" />
									</button>
								)}
							</div>
						</div>

						{/* Right Side: Discard button */}
						<div className="flex items-center justify-end whitespace-nowrap flex-shrink-0 min-w-[120px]">
							{Object.keys(updatedRoles).length > 0 && (
								<button
									onClick={handleDiscard}
									disabled={isLoading}
									className="text-xs font-bold bg-red-50 hover:bg-red-100 text-red-600 hover:text-red-700 rounded-xl px-4 py-2 border border-red-100/50 transition-all duration-200 uppercase tracking-wider flex items-center gap-1.5 shadow-sm active:scale-95"
								>
									discard changes
								</button>
							)}
						</div>
					</div>

					{/* Table Wrapper to cleanly fit exactly 4 rows of members */}
					<div className="overflow-x-auto overflow-y-auto max-h-[400px]">
						<table className="min-w-full divide-y divide-gray-100">
							<thead className="sticky top-0 z-20 bg-gray-50 shadow-[0_1px_0_0_rgba(243,244,246,1)]">
								<tr className="bg-gray-50">
									<th className="sticky top-0 z-20 bg-gray-50 px-8 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-widest whitespace-nowrap border-b border-gray-100">
										Member
									</th>
									<th className="sticky top-0 z-20 bg-gray-50 px-4 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-widest whitespace-nowrap border-b border-gray-100">
										Linked QRs
									</th>
									<th className="sticky top-0 z-20 bg-gray-50 px-4 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-widest whitespace-nowrap border-b border-gray-100">
										Status
									</th>
									<th className="sticky top-0 z-20 bg-gray-50 px-2 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-widest whitespace-nowrap border-b border-gray-100">
										Role
									</th>
									<th className="sticky top-0 z-20 bg-gray-50 pl-2 pr-8 py-4 text-right text-xs font-bold text-gray-500 uppercase tracking-widest whitespace-nowrap border-b border-gray-100">
										Actions
									</th>
								</tr>
							</thead>

							<tbody className="divide-y divide-gray-100 bg-white">
								{(() => {
									if (isListLoading) {
										return (
											<tr>
												<td colSpan={5} className="py-16 text-center">
													<div className="flex flex-col items-center justify-center gap-3">
														<Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
														<p className="text-sm font-semibold text-gray-500">
															Loading team directory...
														</p>
													</div>
												</td>
											</tr>
										);
									}

									const filteredMembers = members.filter((member) => {
										const name = (member.name || "").toLowerCase();
										const firstName = (member.firstName || "").toLowerCase();
										const lastName = (member.lastName || "").toLowerCase();
										const email = (member.email || "").toLowerCase();
										const query = searchQuery.toLowerCase().trim();
										return name.includes(query) || firstName.includes(query) || lastName.includes(query) || email.includes(query);
									});

									if (filteredMembers.length === 0) {
										return (
											<tr>
												<td colSpan={5} className="py-12 text-center">
													<p className="text-sm font-semibold text-gray-400">
														No members found matching "{searchQuery}"
													</p>
												</td>
											</tr>
										);
									}

									return filteredMembers.map((member) => {
										const linkedQRs = qrs.filter(qr => qr.userId === member._id);
										return (
										<tr key={member._id} className="hover:bg-gray-50/50 transition-colors">
											{/* Member */}
											<td className="px-8 py-5">
												<div className="flex items-center gap-4">
													<div className="relative flex-shrink-0">
														{member.avatar ? (
															<img
																src={member.avatar}
																alt=""
																className="h-10 w-10 rounded-2xl object-cover ring-2 ring-gray-100"
															/>
														) : (
															<div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-gradient-to-br from-gray-100 to-gray-200 text-sm font-bold text-gray-600 ring-2 ring-gray-100">
																{member?.name?.[0] || (member?.firstName?.[0] || member?.email?.[0] || "?")}
															</div>
														)}
														{member._id === user?._id && (
															<span className="absolute -bottom-0.5 -right-0.5 flex h-2.5 w-2.5 rounded-full bg-rose-500 ring-2 ring-white" />
														)}
													</div>
													<div className="flex flex-col min-w-0">
														<div className="flex items-center gap-2 max-w-full">
															<TruncatedText 
																text={member.name || (member.firstName ? `${member.firstName} ${member.lastName || ""}`.trim() : member.email)}
																className="text-sm font-bold text-gray-900 leading-none mb-1"
																maxClass="max-w-[120px] sm:max-w-[180px]"
															/>
															{canManageTeam && member.type === "invited" && (
																<button
																	onClick={() => resendInvite(member.email)}
																	className="text-[11px] font-bold text-indigo-600 hover:text-indigo-800 transition-colors whitespace-nowrap mb-1"
																	title="Resend Invitation Email"
																>
																	Resend
																</button>
															)}
														</div>
														<TruncatedText 
															text={member.email}
															className="text-xs text-gray-500 font-medium"
															maxClass="max-w-[150px] sm:max-w-[220px]"
														/>
													</div>
												</div>
											</td>

											{/* Linked QRs */}
											<td className="px-4 py-5">
												<div className="flex flex-wrap items-center gap-2">
													{linkedQRs.length > 0 ? (
														linkedQRs.map(qr => (
															<span key={qr._id} className="group/qr inline-flex items-center gap-1 rounded-lg bg-indigo-50 px-2.5 py-1 text-[10px] font-bold text-indigo-700 ring-1 ring-inset ring-indigo-600/10 transition-all hover:bg-indigo-100 whitespace-nowrap" title={qr.fullQRLink}>
																QR-{qr.shortCode}
																<button
																	onClick={() => handleUnlinkQR(qr._id)}
																	className="ml-1 text-indigo-300 hover:text-red-500 transition-colors opacity-0 group-hover/qr:opacity-100"
																>
																	<XMarkIcon className="h-3 w-3" />
																</button>
															</span>
														))
													) : (
														<span className="text-[11px] text-gray-400 font-medium italic whitespace-nowrap">
															No linked QRs
														</span>
													)}
													<button
														onClick={() => {
															setSelectedMember(member);
															setIsLinkQROpen(true);
														}}
														className="inline-flex h-6 w-6 items-center justify-center rounded-lg bg-gray-50 text-gray-400 hover:bg-indigo-600 hover:text-white transition-all shadow-sm flex-shrink-0"
														title="Link a QR code"
													>
														<PlusIcon className="h-3.5 w-3.5" />
													</button>
												</div>
											</td>

											{/* Status */}
											<td className="px-4 py-5">
												{member.active ? (
													<span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-[11px] font-bold text-emerald-700 ring-1 ring-inset ring-emerald-600/20 whitespace-nowrap">
														<span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
														Active
													</span>
												) : (
													<span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1 text-[11px] font-bold text-amber-700 ring-1 ring-inset ring-amber-600/20 whitespace-nowrap">
														<span className="h-1.5 w-1.5 rounded-full bg-amber-500 shadow-sm" />
														Pending
													</span>
												)}
											</td>

											{/* Role */}
											<td className="px-2 py-5">
												<div className="whitespace-nowrap">
													{(member.isInitialCreatorAdmin || !canManageTeam) ? (
														<RoleBadge role={member.role} />
													) : (
														<RoleDropdown
															value={member.role}
															onChange={(v) => updateAccess(member._id, v, member)}
														/>
													)}
												</div>
											</td>

											{/* Actions */}
											<td className="pl-2 pr-8 py-5 text-right">
												<div className="flex items-center justify-end gap-3 whitespace-nowrap">
													{canManageTeam && !member.isInitialCreatorAdmin && (
														<button
															onClick={() => handleRemoveMember(member)}
															className="group rounded-xl bg-red-50 p-2 text-red-500 hover:bg-red-500 hover:text-white transition-all duration-200"
															title="Remove member"
														>
															<Trash className="h-4 w-4" />
														</button>
													)}
												</div>
											</td>
										</tr>
									);
								})})()}
							</tbody>
						</table>
					</div>
				</div>
			</div>

			{/* Link QR Modal */}
			{isLinkQROpen && (
				<div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-300">
					<div className="w-full max-w-md rounded-3xl bg-white p-8 shadow-2xl animate-in zoom-in-95 duration-200">
						<div className="flex items-center justify-between mb-6">
							<div>
								<h3 className="text-xl font-bold text-gray-900">
									Link QR Code
								</h3>
								<p className="text-sm text-gray-500 mt-1">
									Assign a QR code to <span className="font-bold text-indigo-600">
										{selectedMember?.name || (selectedMember?.firstName ? `${selectedMember.firstName} ${selectedMember.lastName || ""}`.trim() : selectedMember?.email)}
									</span>
								</p>
							</div>
							<button
								onClick={() => setIsLinkQROpen(false)}
								className="rounded-xl p-2 bg-gray-50 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-all"
							>
								<XMarkIcon className="h-5 w-5" />
							</button>
						</div>

						<div className="space-y-3 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
							{qrs.filter(q => !q.userId).length > 0 ? (
								qrs.filter(q => !q.userId).map((qr) => (
									<button
										key={qr._id}
										onClick={() => handleLinkQR(qr._id)}
										disabled={isLoading}
										className="w-full group flex items-center justify-between rounded-2xl border border-gray-100 bg-gray-50 p-4 text-left hover:border-indigo-200 hover:bg-indigo-50 transition-all"
									>
										<div className="flex flex-col">
											<span className="text-sm font-bold text-gray-900 group-hover:text-indigo-700">
												QR-{qr.shortCode}
											</span>
											<span className="text-[11px] text-gray-500 font-mono mt-0.5">
												{qr.fullQRLink}
											</span>
										</div>
										<div className="h-8 w-8 rounded-xl bg-white border border-gray-100 flex items-center justify-center text-gray-400 group-hover:bg-indigo-600 group-hover:text-white transition-all shadow-sm">
											<PlusIcon className="h-4 w-4" />
										</div>
									</button>
								))
							) : (
								<div className="text-center py-12 px-4 rounded-3xl bg-gray-50 border border-dashed border-gray-200">
									<div className="h-12 w-12 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
										<PlusIcon className="h-6 w-6 text-gray-300" />
									</div>
									<p className="text-sm font-medium text-gray-500">No unassigned QRs found</p>
									<p className="text-xs text-gray-400 mt-1">Assign a property to a QR first in QR Management</p>
								</div>
							)}
						</div>

						<div className="mt-8 flex justify-center">
							<button
								onClick={() => setIsLinkQROpen(false)}
								className="px-6 py-2.5 text-sm font-bold text-gray-500 hover:text-gray-700 transition-colors"
							>
								Close
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Invite Modal */}
			{isInviteOpen && (
				<div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-300">
					<div className="w-full max-w-lg rounded-3xl bg-white p-8 shadow-2xl animate-in zoom-in-95 duration-200">
						<div className="flex items-center justify-between mb-8">
							<div>
								<h3 className="text-xl font-bold text-gray-900">
									Invite Team Member
								</h3>
								<p className="text-sm text-gray-500 mt-1">
									Add collaborators to your property.
								</p>
							</div>
							<button
								onClick={() => {
									setIsInviteOpen(false);
									setEmails("");
									setInviteRole("");
								}}
								className="rounded-xl p-2 bg-gray-50 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-all"
							>
								<XMarkIcon className="h-5 w-5" />
							</button>
						</div>

						<div className="space-y-6">
							<div>
								<label className="block text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-2 px-1">
									Email Address
								</label>
								<input
									type="email"
									value={emails}
									onChange={(e) => setEmails(e.target.value)}
									placeholder="jane@example.com"
									className={`w-full rounded-2xl border px-5 py-4 text-sm font-medium focus:bg-white focus:outline-none focus:ring-4 transition-all ${showEmailError
										? "border-red-300 bg-red-50/10 focus:border-red-500 focus:ring-red-500/10"
										: "border-gray-100 bg-gray-50 focus:border-indigo-500 focus:ring-indigo-500/10"
										}`}
								/>
								{showEmailError && (
									<p className="mt-1.5 text-xs text-red-500 font-medium px-1">
										Please enter a valid email address (e.g. name@domain.com)
									</p>
								)}
							</div>

							<div>
								<label className="block text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-2 px-1">
									Assign Role
								</label>
								<div className="flex flex-col gap-2">
									<div className="flex gap-2">
										{Object.entries(MEMBER_ROLES).map(([key, val]) => {
											if (val === "admin" && loggedInRole !== "admin") return null;
											const isSelected = inviteRole === val;
											return (
												<button
													key={key}
													onClick={() => setInviteRole(val)}
													className={`flex-1 rounded-xl py-3 text-[10px] font-bold transition-all duration-200 uppercase whitespace-nowrap ${isSelected
														? SELECTED_ROLE_BUTTON_STYLES[val]
														: "bg-gray-50 text-gray-400 border border-gray-100 hover:bg-gray-100 hover:text-gray-600 hover:border-gray-200"
														}`}
												>
													{val.replace("_", " ").toUpperCase()}
												</button>
											);
										})}
									</div>
									{inviteRole && (
										<p className="text-xs text-gray-500 mt-2 px-1">
											<span className="font-bold text-gray-700">{inviteRole.replace("_", " ").toUpperCase()}:</span> {ROLE_DESCRIPTIONS[inviteRole]}
										</p>
									)}
								</div>
							</div>

							<button
								onClick={handleInvite}
								disabled={isLoading || !isValidEmailFormat || !emails.trim() || !inviteRole}
								className={`w-full rounded-2xl py-4 text-sm font-bold text-white shadow-lg transition-all duration-200 transform active:scale-95 ${isLoading || !isValidEmailFormat || !emails.trim() || !inviteRole
									? "bg-gray-200 text-gray-400 cursor-not-allowed shadow-none"
									: "bg-indigo-600 hover:bg-indigo-700 shadow-indigo-200"
									}`}
							>
								{isLoading ? (
									<div className="flex items-center justify-center gap-2">
										<Loader2 className="h-4 w-4 animate-spin" />
										Sending...
									</div>
								) : (
									"Send Invitations"
								)}
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Remove Member Confirmation Modal */}
			{isRemoveModalOpen && (
				<div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-300">
					<div className="w-full max-w-sm rounded-[2rem] bg-white p-8 shadow-2xl animate-in zoom-in-95 duration-200 border border-gray-100">
						<div className="text-center">
							<div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-red-50 text-red-500 mb-6 ring-4 ring-red-50">
								<Trash className="h-8 w-8" />
							</div>
							<h3 className="text-xl font-bold text-gray-900 mb-2">
								Remove Member?
							</h3>
							<p className="text-sm text-gray-500 mb-8 leading-relaxed">
								Are you sure you want to remove <span className="font-bold text-gray-900">{memberToRemove?.name || memberToRemove?.email}</span>?
								This will revoke their access to this property immediately.
							</p>
						</div>

						<div className="flex flex-col gap-3">
							<button
								onClick={confirmRemove}
								disabled={isLoading}
								className="w-full rounded-2xl bg-red-500 py-4 text-sm font-bold text-white shadow-lg shadow-red-200 hover:bg-red-600 transition-all duration-200 transform active:scale-95 disabled:opacity-50"
							>
								{isLoading ? (
									<div className="flex items-center justify-center gap-2">
										<Loader2 className="h-4 w-4 animate-spin" />
										Removing...
									</div>
								) : (
									"Yes, Remove Member"
								)}
							</button>
							<button
								onClick={() => {
									setIsRemoveModalOpen(false);
									setMemberToRemove(null);
								}}
								disabled={isLoading}
								className="w-full rounded-2xl bg-gray-50 py-4 text-sm font-bold text-gray-500 hover:bg-gray-100 transition-all duration-200 transform active:scale-95"
							>
								Cancel
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
